<template>
  <div id="branchMediaSelector">

    <div class="file-block" v-for="(file, fileIndex) of filteredFiles" :key="'file-' + fileIndex"
         :class="{image: isImage(file), video: isVideo(file), document: isDocument(file), selected: isSelected(file)}" @click="changeSelected(file)">
      <div class="file">
        <svgicon class="checkmark" v-if="isSelected(file)" src="icon/success-blank.svg" :height="22" :width="22"></svgicon>
        <template v-if="isImage(file)">
          <div class="image-holder" :style="{backgroundImage: 'url(\'' + getAssetUrl(file) + '\')'}"></div>
        </template>
        <template v-if="isVideo(file)">
          <video :src="getAssetUrl(file)" controls></video>
        </template>
        <template v-if="isDocument(file)">
          <svgicon src="icon/attachment.svg" :height="50" :width="50"></svgicon>
        </template>
      </div>
      <div class="name">{{file.name}}</div>
    </div>

    <div class="file-block file-block--no-file" v-if="noFiles">
      <svgicon src="icon/image-missing.svg" :height="30" :width="30"></svgicon>
      <div class="file-block__warning">
        <div class="file-block__warning--header">{{$t('media_manager.no_files_header_' + mediaType)}}</div>
        <router-link :to="{ path: '/media-manager', query: {branch: $route.query.branch}}" class="access-lib" target="_blank" >
          {{$t('media_manager.no_files_' + mediaType)}}
          <svgicon src="icon/arrow-right.svg" :height="13" :width="13"></svgicon>
        </router-link>
      </div>
    </div>

    <router-link :to="{ path: '/media-manager', query: {branch: $route.query.branch}}" class="access-lib access-lib--absolute" target="_blank" v-if="!noFiles">
      {{$t('media_manager.access_lib')}}
      <svgicon src="icon/arrow-right.svg" :height="13" :width="13"></svgicon>
    </router-link>

  </div>
</template>

<script>
  import { mapActions } from "vuex";

  export default {
    name: "branchMediaSelector",

    props: {
      image: Boolean,
      multiple: Boolean,
      document: Boolean,
      selected: null,
      video: Boolean
    },

    data() {
      return {
        files: []
      }
    },

    computed: {
      branchId() {
        return this.$route.query.branch;
      },
      filteredFiles() {
        if (this.image) {
          return this.files.filter(f => this.isImage(f));
        }
        if (this.video) {
          return this.files.filter(f => this.isVideo(f));
        }
        if (this.document) {
          return this.files.filter(f => this.isDocument(f));
        }
        return this.files.filter(f => !this.isDocument(f));
      },
      noFiles() {
        return !this.filteredFiles || this.filteredFiles.length < 1;
      },
      mediaType() {
        if (this.image) {
          return "image";
        }
        if (this.video) {
          return "video";
        }
        if (this.document) {
          return "document";
        }
        return "multiple";
      }
    },

    async mounted() {
      await this.getBranchFiles();
    },

    methods: {
      ...mapActions(["getBranchImages"]),

      async getBranchFiles() {
        let response = await this.getBranchImages({branchId: this.branchId});
        if (response && response.data) {
          this.files = response.data;
        }
      },

      changeSelected(file) {
        let selected;
        if (this.multiple) {
          if (!this.selected) {
            selected = [];
          } else {
            selected = [...this.selected];
          }
          let index = selected.indexOf(file.id);
          if (index > -1) {
            selected.splice(index, 1);
          } else {
            selected.push(file.id);
          }
        } else {
          if (this.selected === file.id) {
            selected = null;
          } else {
            selected = file.id;
          }
        }
        this.$emit("changed", selected);
      },

      getAssetUrl(file) {
        return Parameters.urlFileServer + file.url;
      },

      isImage(file) {
        return file.type.indexOf("image") > -1;
      },

      isDocument(file) {
        let types = ["application/pdf"];
        return types.indexOf(file.type) > -1;
      },

      getFileWithId(id) {
        return this.files.find(f => f.id === id);
      },

      isSelected(file) {
        if (!this.selected) {
          return false;
        }
        if (this.multiple) {
          return this.selected.indexOf(file.id) > -1;
        } else {
          return this.selected === file.id;
        }
      },

      isVideo(file) {
        return file.type.indexOf("video") > -1;
      }
    }
  }
</script>

<style lang="scss" scoped>
  $size: 160px;

  #branchMediaSelector {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    padding-bottom: 22px;
  }

  .access-lib {
    display: flex;
    align-items: center;
    color: #888;
    fill: #888;
    text-decoration: none;
    font-size: 12px;
    transition: all 250ms ease;
    flex-wrap: nowrap;
    &--absolute {
      position: absolute;
      bottom: 0;
      right: 0;
      padding-bottom: 5px;
    }
    &:hover {
      color: var(--secondary-color);
      fill: var(--secondary-color);
    }
  }

  .checkmark {
    position: absolute;
    top: 6px;
    right: 6px;
    fill: var(--primary-color);
    z-index: 2;
  }

  .file-block {
    display: flex;
    flex-direction: column;
    margin: 5px;
    cursor: pointer;

    &.selected {
      .file {
        border-color: var(--primary-color);
        box-shadow: inset 0px 0px 0px 1px var(--primary-color);
      }

      .name {
        font-weight: bold;
        color: #404040;
      }
    }

    &.document {

      .file {
        width: 240px;
        height: 90px;
      }
    }

    &.file-block--no-file {
      font-size: 14px;
      cursor: default;
      flex-direction: row;
      color: #404040;
      padding: 10px 10px 0 10px;

      .file-block__warning {
        padding-left: 5px;
      }
      .file-block__warning--header {
        font-weight: bold;
        color: var(--primary-color);
      }
    }
  }

  .file {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    flex-grow: 1;
    width: $size;
    height: $size;
    max-height: $size;
    max-width: $size;
    flex-shrink: 0;
    border: 1px solid #ccc;
    border-radius: 10px;
    padding: 10px;
  }

  .name {
    font-size: 11px;
    padding-top: 8px;
    text-align: center;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    line-height: 1;
    max-width: $size;
    color: #888;
  }

  .image-holder {
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }

  video {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: calc(#{$size} - 20px);
  }
</style>
